import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * First-run onboarding status for the signed-in user
 * (docs — customer onboarding). `completed` is false only for a genuinely new
 * customer user (customer_users.onboarding_completed_at IS NULL); existing
 * users were grandfathered at migration time. Drives the /welcome diversion.
 */
export function useOnboardingStatus() {
  return useQuery({
    queryKey: ['onboarding-status'],
    queryFn: async (): Promise<{ completed: boolean; hasRow: boolean }> => {
      const { data: authData } = await supabase.auth.getUser();
      const uid = authData.user?.id;
      // Not signed in → nothing to divert.
      if (!uid) return { completed: true, hasRow: false };
      const { data, error } = await supabase
        .schema('orders')
        .from('customer_users')
        .select('onboarding_completed_at')
        .eq('id', uid)
        .maybeSingle();
      // On error or no profile row, don't trap the user in onboarding.
      if (error || !data) return { completed: true, hasRow: false };
      return { completed: data.onboarding_completed_at != null, hasRow: true };
    },
    staleTime: 60 * 1000,
  });
}

/** Mark the caller's first-run onboarding complete. */
export function useCompleteOnboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase.schema('orders').rpc('complete_onboarding');
      if (error) throw error;
    },
    onSuccess: () => {
      // ⚠ Write the answer into the cache BEFORE the caller navigates. An
      // invalidate alone leaves the old `completed:false` in place until the
      // refetch lands, so AppShell's guard sent a brand-new customer straight
      // back to /welcome — remounting the wizard with their answer wiped and
      // "Go to my dashboard" disabled again.
      qc.setQueryData(['onboarding-status'], { completed: true, hasRow: true });
      void qc.invalidateQueries({ queryKey: ['onboarding-status'] });
    },
  });
}
